/* eslint-disable no-continue */
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import _ from "lodash";
import { Icon } from "@iconify/react";
import { Input, Label } from "../components/shared/InputField";
import {
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  HStack,
  VStack,
} from "../components/utils";
import { formatDisplayDate, parseDateString } from "../utils/date";
import { ForfeitureSummaryCard } from "../components/shared/ForfeitureSummaryCard";
import { ExitDateBasis } from "../types/EsopPlan";
import { useGetEsopPlanDetails } from "../queries/esopPlan";

type UpdateLastWorkingDateDialogProps = {
  onClose: () => void;
  onSubmit: (lastWorkingDate: string) => void;
  currentLastWorkingDate?: string;
  vestingData: any[];
  grantsData: any[];
  separationReason?: string;
};

function PlanForfeitureSummary({
  planId,
  planName,
  separationReason,
  lastDay,
}: {
  planId: string;
  planName: string;
  separationReason: string;
  lastDay?: Date;
}) {
  const { data: plan } = useGetEsopPlanDetails(planId, !!planId);
  if (!plan?.forfeitureSettings) return null;
  // Only show here when the plan uses last working date (or has no explicit basis set)
  if (plan.forfeitureSettings.exitDateBasis === ExitDateBasis.RESIGNATION_DATE)
    return null;
  return (
    <ForfeitureSummaryCard
      forfeitureSettings={plan.forfeitureSettings}
      separationReason={separationReason}
      lastDay={lastDay}
      planName={planName}
    />
  );
}

interface UpdateLastWorkingDateValues {
  lastWorkingDate: string;
}

export function UpdateLastWorkingDateDialog(
  props: UpdateLastWorkingDateDialogProps
) {
  const {
    onClose,
    onSubmit,
    currentLastWorkingDate,
    vestingData,
    grantsData,
    separationReason,
  } = props;

  const uniquePlanIds = useMemo(() => {
    if (!grantsData) return [];
    const seen = new Set<string>();
    const result: { planId: string; planName: string }[] = [];
    for (const grant of grantsData) {
      const pid = grant.esopPlanId;
      if (pid && !seen.has(pid)) {
        seen.add(pid);
        result.push({ planId: pid, planName: grant.planName ?? "" });
      }
    }
    return result;
  }, [grantsData]);

  const intialValues = {
    lastWorkingDate: currentLastWorkingDate || "",
  };

  const validationSchema = Yup.object().shape({
    lastWorkingDate: Yup.string()
      .required("Last Working Date is required")
      .test("is-valid-date", "Please enter a valid date", (value) => {
        if (!value) return false;
        const date = new Date(value);
        return date.toString() !== "Invalid Date";
      }),
  });

  const formik = useFormik({
    initialValues: intialValues,
    validationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [revertingData, setRevertingData] = useState<
    { identifier: string; options: number; grantDate?: Date }[]
  >([]);

  useEffect(() => {
    setRevertingData([]);
    setError(null);
    setIsConfirmed(false);
    // Calculate reverting data based on lastWorkingDate
    if (vestingData && vestingData.length > 0) {
      const groupedData = _.groupBy(vestingData, (v) => v.identifier);

      Object.entries(groupedData).forEach(([grantId, transactions]) => {
        const vestedOptions = _.sumBy(
          transactions.filter((t) => t.transactionType === "VESTED"),
          (et) => et.options
        );

        const exercisedAndForfeitedOptions = _.sumBy(
          transactions.filter(
            (t) =>
              t.transactionType === "CASHED_OUT" ||
              t.transactionType === "STOCK_EXERCISE" ||
              t.transactionType === "SURRENDERED" ||
              t.transactionType === "CASH_EXERCISE_REQUESTED" ||
              t.transactionType === "STOCK_EXERCISE_REQUESTED"
          ),
          (et) => et.options
        );

        let totalRevertingInGrant = 0;

        transactions.forEach((pv) => {
          if (
            pv.transactionType === "VESTED" &&
            new Date(pv.date) > new Date(formik.values.lastWorkingDate)
          ) {
            totalRevertingInGrant += pv.options;
          }
        });

        if (totalRevertingInGrant > 0) {
          if (
            totalRevertingInGrant >
            vestedOptions - exercisedAndForfeitedOptions
          ) {
            setError(
              `The Grant with Id ${grantId} has more exercised and forfeited options than Reverted Vested options. Please check the data.`
            );
          }
          const grant = grantsData?.find((g) => g.identifier === grantId);
          setRevertingData((prev) => [
            ...prev,
            {
              identifier: grant?.identifier || "",
              options: totalRevertingInGrant,
              grantDate: new Date(grant?.grantDate || ""),
            },
          ]);
        }
      });
    }
  }, [formik.values.lastWorkingDate]);

  const handleSubmit = (values: UpdateLastWorkingDateValues) => {
    if (error) {
      toast(error, {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    if (revertingData && revertingData.length > 0) {
      if (!isConfirmed || confirmationInput !== "Resign") {
        toast("Please confirm the changes to proceed", {
          type: "error",
          autoClose: 2000,
        });
        return;
      }
    }

    onSubmit(values.lastWorkingDate);
  };

  return (
    <>
      <div className="">
        <div className="px-10 text-lg font-medium border-b py-7">
          <h6 className="flex justify-between">
            Update Last Working Date{" "}
            <span onClick={() => onClose()} className="cursor-pointer">
              X
            </span>{" "}
          </h6>
        </div>
        <VStack className="w-full px-10 py-7 gap-11">
          <form onSubmit={formik.handleSubmit}>
            <VStack className="flex-wrap gap-4">
              <div className="py-2">
                Please enter the Last Working Date to continue.
              </div>
              <HStack className="items-start w-full">
                <Label className="w-48 text-sm font-normal whitespace-nowrap">
                  Last Working Date
                </Label>
                <VStack className="gap-1 flex-1">
                  <Input
                    type="date"
                    className="w-full max-w-md"
                    {...formik.getFieldProps("lastWorkingDate")}
                  />
                  {formik.touched.lastWorkingDate &&
                    formik.errors.lastWorkingDate && (
                      <Error text={String(formik.errors.lastWorkingDate)} />
                    )}
                </VStack>
              </HStack>
            </VStack>

            {separationReason && uniquePlanIds.length > 0 && (
              <VStack className="gap-2 mt-4">
                {uniquePlanIds.map(({ planId, planName }) => (
                  <PlanForfeitureSummary
                    key={planId}
                    planId={planId}
                    planName={planName}
                    separationReason={separationReason}
                    lastDay={
                      formik.values.lastWorkingDate
                        ? parseDateString(formik.values.lastWorkingDate)
                        : undefined
                    }
                  />
                ))}
              </VStack>
            )}

            {(revertingData?.length || 0) > 0 && (
              <VStack className="justify-center gap-4 py-2 text-xs italic text-gray-500">
                <HStack className="bg-[#FFF3C7] p-2 rounded-md gap-2 text-[#BD7D00] items-center">
                  <Icon
                    icon={"fe:warning"}
                    color={"#BD7D00"}
                    height={32}
                    width={32}
                  />
                  <p className="justify-center">
                    {revertingData?.length > 0 &&
                      `Please Note - Vesting activities have happened after the given Last Working Date. `}
                    The following grants will be affected as these activities
                    have happened after the given Last Working Date. If you
                    choose to proceed, the following grants will be affected as
                    follows:
                  </p>
                </HStack>
                <VStack className="px-4 py-4">
                  <table className="w-full text-sm border border-collapse border-gray-300 table-auto">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-2 text-left border border-gray-300">
                          Grant Info
                        </th>
                        <th className="px-4 py-2 text-left border border-gray-300">
                          Grant Date
                        </th>
                        <th className="px-4 py-2 text-left border border-gray-300">
                          Effect on Grant
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {revertingData.map((item) => (
                        <tr key={item.identifier} className="hover:bg-gray-50">
                          <td className="px-4 py-2 border border-gray-300">
                            {item.identifier}
                          </td>
                          <td className="px-4 py-2 border border-gray-300">
                            {item.grantDate
                              ? formatDisplayDate(item.grantDate)
                              : "—"}
                          </td>
                          <td className="px-4 py-2 border border-gray-300">
                            {parseFloat(item.options.toFixed(4))} vested options
                            will be reverted
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </VStack>

                {revertingData && revertingData.length > 0 && (
                  <>
                    <VStack className="items-start gap-2 pt-4">
                      <HStack className="gap-6 justify-items-start">
                        <VStack className="p-1">
                          <Label className="">
                            Please Type <b>Resign</b> exactly to proceed
                          </Label>
                          <p className="text-[#8C8C8C] text-xs">
                            This is done as a security check to ensure any false
                            resignation
                          </p>
                        </VStack>
                        <Input
                          type="text"
                          id="confirmation-input"
                          value={confirmationInput}
                          onChange={(e) => setConfirmationInput(e.target.value)}
                          onPaste={(e) => e.preventDefault()}
                          placeholder="Type 'Resign' to confirm"
                          className="p-2 px-6 text-sm border rounded-md w-72"
                        />
                      </HStack>
                      {confirmationInput !== "Resign" &&
                        confirmationInput.length > 0 && (
                          <p className="text-xs text-red-500">
                            Please type <b>Resign</b> exactly to proceed.
                          </p>
                        )}
                    </VStack>
                    <HStack className="items-center gap-2 pt-4">
                      <input
                        type="checkbox"
                        id="confirmation-checkbox"
                        checked={isConfirmed}
                        onChange={(e) => setIsConfirmed(e.target.checked)}
                      />
                      <label
                        htmlFor="confirmation-checkbox"
                        className="text-sm text-[#464E5F]"
                      >
                        I confirm that I understand the above changes and wish
                        to proceed.
                      </label>
                    </HStack>
                  </>
                )}
              </VStack>
            )}

            <HStack className="justify-between mt-4">
              <ButtonPrimary1 onClick={onClose}>Cancel</ButtonPrimary1>
              <ButtonPrimary
                type="submit"
                disabled={
                  revertingData &&
                  revertingData.length > 0 &&
                  (!isConfirmed || confirmationInput !== "Resign")
                }
              >
                Save
              </ButtonPrimary>
            </HStack>
          </form>
        </VStack>
      </div>
    </>
  );
}
